/* =========================================================================
   Penny Premium 3D — predictive-search.js
   Live suggestions for the header search: debounced (250ms) fetches of
   the predictive-search section (server-rendered Liquid — formatted
   prices, real images), stale requests aborted, skeleton with reserved
   height, arrow/Enter/Escape keyboard navigation, aria-live counts.
   Any fetch error falls back silently to the plain form submit.
   ========================================================================= */

const rootRoute = window.Shopify?.routes?.root ?? '/';
const panel = document.querySelector('[data-search-panel]');
const input = panel?.querySelector('input[type="search"]');
const results = panel?.querySelector('[data-predictive-results]');
const announce = panel?.querySelector('[data-predictive-announce]');

if (panel && input && results) {
  let timer = null;
  let controller = null;
  let activeIndex = -1;

  const options = () => Array.from(results.querySelectorAll('[role="option"]'));

  const clear = () => {
    results.replaceChildren();
    results.setAttribute('hidden', '');
    input.removeAttribute('aria-activedescendant');
    activeIndex = -1;
  };

  const setActive = (index) => {
    const opts = options();
    if (opts.length === 0) return;
    activeIndex = (index + opts.length) % opts.length;
    opts.forEach((opt, i) => {
      opt.classList.toggle('is-active', i === activeIndex);
      if (i === activeIndex) {
        opt.id = opt.id || `PredictiveOption-${i}`;
        input.setAttribute('aria-activedescendant', opt.id);
        opt.scrollIntoView({ block: 'nearest' });
      }
    });
  };

  const search = async (query) => {
    controller?.abort();
    controller = new AbortController();

    try {
      const url =
        `${rootRoute}search/suggest?q=${encodeURIComponent(query)}` +
        `&resources[type]=product,collection,page&resources[limit]=6` +
        `&section_id=predictive-search`;
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(response.statusText);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.querySelector('[data-predictive-inner]');
      if (!fresh) throw new Error('empty');

      results.replaceChildren(fresh);
      results.removeAttribute('hidden');
      activeIndex = -1;

      if (announce) {
        const count = fresh.querySelector('[data-predictive-count]')?.textContent.trim() ?? '0';
        announce.textContent = announce.dataset.template.replace('[count]', count);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      clear(); // silent fallback — the form still submits normally
    }
  };

  input.addEventListener('input', () => {
    window.clearTimeout(timer);
    const query = input.value.trim();
    if (query.length < 2) {
      clear();
      return;
    }
    // Skeleton with reserved height while the (debounced) request runs
    if (results.hasAttribute('hidden')) {
      results.innerHTML =
        '<div class="predictive__skeleton" aria-hidden="true">' +
        '<span class="skeleton-card__line"></span><span class="skeleton-card__line"></span><span class="skeleton-card__line"></span>' +
        '</div>';
      results.removeAttribute('hidden');
    }
    timer = window.setTimeout(() => search(query), 250);
  });

  input.addEventListener('keydown', (event) => {
    if (results.hasAttribute('hidden')) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive(activeIndex + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(activeIndex - 1);
    } else if (event.key === 'Enter' && activeIndex > -1) {
      event.preventDefault();
      options()[activeIndex]?.click();
    } else if (event.key === 'Escape') {
      clear();
    }
  });

  // Close suggestions when the search panel closes or focus leaves
  document.addEventListener('click', (event) => {
    if (!panel.contains(event.target)) clear();
  });
}
